'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

import { useCartService } from '@/hooks/useCartService';
import { useShippingService } from '@/hooks/useShippingService';
import { analyticsBeginCheckout } from '@/lib/analytics';
import { logger } from '@/lib/logger.client';
import { pixelTrackInitiateCheckout } from '@/lib/pixel';
import { ICoupon } from '@/lib/schemas/Coupon.zod';
import { IOrder, IShippingLocation, IShippingProvider } from '@/services/types';
import { calcBuyXGetYCartDiscount } from '@/themes/cray26/services/math';
import { CHECKOUT_KEYS } from '@/types/checkout';

import CartCoupon from './CartCoupon';
import { CartShippingMethodSelector, ShippingMethod, mapProviderToShippingMethod } from './CartShippingMethodSelector';

// Currency formatter utility
const toCurrencyFormat = (value: number, currency: string = 'ARS') => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency,
  }).format(Number(value));
};

type CartItem = IOrder;

/**
 * Returns the applicable base price for a cart item based on its price type.
 * Follows the dynamic pricing model: public price by default, with optional
 * reseller/wholesaler override values available on the product record.
 * Uses the flat price fields on Product matching the Zod schema.
 */
const getPriceForItem = (item: CartItem): number => {
  const { price_type, product } = item;
  switch (price_type) {
    case 'PUBLIC':
      return product.price_public_value ?? 0;
    case 'WHOLESALE':
      return product.price_wholesale_value ?? 0;
    case 'RETAIL':
      return product.price_retail_value ?? 0;
    default:
      return product.price_public_value ?? 0;
  }
};

type CartOrderSummaryProps = {
  cartItems?: CartItem[];
};

export const CartOrderSummary: React.FC<CartOrderSummaryProps> = ({ cartItems: externalItems }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<ICoupon | null>(null);
  const [selectedShipping, setSelectedShipping] = useState<ShippingMethod | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<IShippingLocation | null>(null);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const router = useRouter();
  const shippingService = useShippingService();
  const cartService = useCartService();

  const loadShippingProviders = useCallback(async (subtotal: number = 0) => {
    try {
      const response = await shippingService.getShippingProviders();
      const providers = response ?? [];
      const activeMethods = (providers as IShippingProvider[]).filter((p) => p.status === 'ACTIVE').map((p) => mapProviderToShippingMethod(p, subtotal));
      setShippingMethods(activeMethods);

      // Restore previously chosen method by matching stored methodId
      try {
        const raw = sessionStorage.getItem(CHECKOUT_KEYS.SHIPPING);
        if (raw) {
          const stored = JSON.parse(raw) as { methodId?: string };
          const match = activeMethods.find((m) => m.id === stored.methodId);
          if (match) {
            setSelectedShipping(match);
            logger.info({ msg: 'CartOrderSummary: restored shipping method', methodId: stored.methodId });
            return;
          }
        }
      } catch (_) {
        /* ignore – fall through to auto-select */
      }

      // Auto-select the first method when nothing is restored
      if (activeMethods.length > 0 && !selectedShipping?.id) {
        setSelectedShipping(activeMethods[0]);
      }
      logger.info({ msg: 'CartOrderSummary: shipping providers loaded', count: activeMethods.length });
    } catch (error) {
      logger.error({ msg: 'CartOrderSummary: failed to load shipping providers', error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let items: CartItem[];
    if (externalItems && externalItems.length > 0) {
      setCartItems(externalItems);
      items = externalItems;
    } else {
      const localItems = cartService.getLocalCartItems();
      setCartItems(localItems);
      items = localItems;
    }
    // Load preloaded shipping selection and provider list
    loadPreloadedShipping();
    // Compute subtotal from items directly — setCartItems is async so cartItems
    // still holds the previous stale value at this point.
    const subtotal = items.reduce((sum, item) => sum + getPriceForItem(item) * (item.quantity || 0), 0);
    loadShippingProviders(subtotal);
  }, [externalItems, loadShippingProviders, cartService]);

  const loadPreloadedShipping = () => {
    try {
      // Read from the unified key; only methodId is stored, match against
      // already-loaded providers once they are available.
      const raw = sessionStorage.getItem(CHECKOUT_KEYS.SHIPPING);
      if (raw) {
        const stored = JSON.parse(raw) as { methodId?: string; location?: IShippingLocation };
        if (stored.location) {
          setSelectedLocation(stored.location);
        }
        // Method matching against providers is handled inside loadShippingProviders
        // via the stored methodId written back from this component.
        logger.info({ msg: 'CartOrderSummary: restored shipping preload from unified key', stored });
      }
    } catch (error) {
      logger.error({ error }, 'Failed to load preloaded shipping data');
    }
  };

  const loadCartItems = () => {
    try {
      setCartItems(cartService.getLocalCartItems());
    } catch (error) {
      logger.error({ error }, 'Failed to load cart items for summary');
    }
  };

  const calculateSubtotal = useCallback(() => {
    return cartItems.reduce((total, item) => {
      return total + getPriceForItem(item) * (item.quantity || 0);
    }, 0);
  }, [cartItems]);

  const calculateCartDiscounts = useCallback(() => {
    const now = new Date();
    let total = 0;

    // Phase 1 — per-item discounts: server net price, PERCENTAGE, FIXED.
    // BUY_X_GET_Y is intentionally skipped here (handled cart-level in phase 2).
    for (const item of cartItems) {
      const basePrice = getPriceForItem(item);

      // Prefer server-provided net price (discounted_price) if it exists.
      if (item.product.discounted_price !== undefined && item.product.discounted_price < basePrice) {
        total += (basePrice - item.product.discounted_price) * (item.quantity || 0);
        continue;
      }

      // Resolve sale from whichever field the API populated.
      const sale = item.product.sale ?? (item.product as any).effective_sale ?? (item.product as any).sales?.[0]?.sale ?? null;
      if (!sale || !item.cart_discounts?.length) continue;

      // Validate sale date range.
      const validFrom = sale.valid_from ? new Date(sale.valid_from) : null;
      const validTo = sale.valid_to ? new Date(sale.valid_to) : null;
      if ((validFrom && now < validFrom) || (validTo && now > validTo)) continue;

      const qty = item.quantity || 0;
      if (sale.discount_type === 'PERCENTAGE') {
        total += basePrice * (sale.discount_value / 100) * qty;
      } else if (sale.discount_type === 'FIXED') {
        total += Math.min(sale.discount_value, basePrice) * qty;
      }
      // BUY_X_GET_Y → phase 2
    }

    // Phase 2 — cart-level BUY_X_GET_Y: group eligible items by sale id,
    // then apply the cheapest-units-free formula across the whole group.
    const buyXGroups = new Map<string, { buyX: number; items: { unitPrice: number; qty: number }[] }>();
    for (const item of cartItems) {
      const sale = item.product.sale ?? (item.product as any).effective_sale ?? (item.product as any).sales?.[0]?.sale ?? null;
      if (!sale || sale.discount_type !== 'BUY_X_GET_Y' || !item.cart_discounts?.length) continue;

      const validFrom = sale.valid_from ? new Date(sale.valid_from) : null;
      const validTo = sale.valid_to ? new Date(sale.valid_to) : null;
      if ((validFrom && now < validFrom) || (validTo && now > validTo)) continue;

      const key = String(sale.id ?? sale.name ?? 'buyxgety');
      if (!buyXGroups.has(key)) {
        buyXGroups.set(key, { buyX: Number(sale.discount_value) || 1, items: [] });
      }
      buyXGroups.get(key)!.items.push({ unitPrice: getPriceForItem(item), qty: item.quantity || 0 });
    }
    for (const { buyX, items } of buyXGroups.values()) {
      total += calcBuyXGetYCartDiscount(items, buyX);
    }

    return total;
  }, [cartItems]);

  const calculateShipping = useCallback(() => {
    return selectedShipping?.cost ?? 0;
  }, [selectedShipping]);

  const calculateTax = useCallback(() => {
    // Tax is already included in the product price.
    // Extracted here for display only (21% IVA / 1.21).
    const afterDiscounts = calculateSubtotal() - calculateCartDiscounts();
    return (afterDiscounts * 0.21) / 1.21;
  }, [calculateSubtotal, calculateCartDiscounts]);

  /**
   * Coupon discount applied to the subtotal (before cart-level discounts).
   * ICoupon.discount_type: 'PERCENTAGE' | 'FIXED_AMOUNT'
   */
  const calculateCouponDiscount = useCallback(() => {
    if (!appliedCoupon) return 0;

    const subtotal = calculateSubtotal();

    if (appliedCoupon.discount_type === 'PERCENTAGE') {
      return subtotal * ((appliedCoupon.discount_value ?? 0) / 100);
    }
    if (appliedCoupon.discount_type === 'FIXED') {
      return Math.min(appliedCoupon.discount_value ?? 0, subtotal);
    }

    return 0;
  }, [appliedCoupon, calculateSubtotal]);

  const handleCouponApplied = (coupon: ICoupon) => {
    setAppliedCoupon(coupon);
  };

  const handleCouponRemoved = () => {
    setAppliedCoupon(null);
  };

  const handleLocationSelected = (location: IShippingLocation) => {
    setSelectedLocation(location);
    logger.info({ msg: 'Shipping location selected', location });
  };

  // Persist shipping selection using unified key – store only the method id
  // and (for TAKEAWAY) the full location object. The full method object is
  // re-fetched from the server on the review page.
  useEffect(() => {
    if (selectedShipping?.id) {
      const shippingData: { methodId: string; location?: IShippingLocation } = {
        methodId: selectedShipping.id,
        ...(selectedLocation ? { location: selectedLocation } : {}),
      };
      sessionStorage.setItem(CHECKOUT_KEYS.SHIPPING, JSON.stringify(shippingData));
      logger.info({ msg: 'CartOrderSummary: shipping selection saved', methodId: selectedShipping.id });
    }
  }, [selectedShipping, selectedLocation]);

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;

    setIsLoading(true);
    try {
      // Save cart items locally with full product data so the cart summary
      // can compute prices correctly throughout the checkout flow.
      cartItems.forEach((item) => cartService.saveLocalCartItem(item));

      // Persist the applied coupon so the review page can restore it.
      if (appliedCoupon) {
        sessionStorage.setItem(CHECKOUT_KEYS.COUPON, JSON.stringify(appliedCoupon));
      } else {
        sessionStorage.removeItem(CHECKOUT_KEYS.COUPON);
      }

      // Fire InitiateCheckout with the real cart total (subtotal + shipping − discounts).
      const checkoutTotal = calculateSubtotal() - calculateCartDiscounts() - calculateCouponDiscount() + calculateShipping();
      pixelTrackInitiateCheckout({
        contentIds: cartItems.map((i) => String(i.product?.id)).filter(Boolean),
        numItems: cartItems.reduce((n, i) => n + (i.quantity || 0), 0),
        value: Math.max(0, checkoutTotal),
      });

      analyticsBeginCheckout({
        contentIds: cartItems.map((i) => String(i.product?.id)).filter(Boolean),
        numItems: cartItems.reduce((n, i) => n + (i.quantity || 0), 0),
        value: Math.max(0, checkoutTotal),
      });

      // All users (logged in or anonymous) go to payment page first
      logger.info({ msg: 'Redirecting to payment page' });
      router.push('/checkout/customer');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorDetails = error instanceof Error ? { message: error.message, stack: error.stack } : error;

      logger.error({
        msg: 'Failed to process checkout',
        error: errorDetails,
        errorType: error?.constructor?.name,
      });

      alert(`Error al procesar el carrito: ${errorMessage}. Por favor, intenta nuevamente.`);
    } finally {
      setIsLoading(false);
    }
  };

  const subtotal = calculateSubtotal();
  const cartDiscounts = calculateCartDiscounts();
  const shipping = calculateShipping();
  const tax = calculateTax();
  const couponDiscount = calculateCouponDiscount();
  const total = subtotal - cartDiscounts - couponDiscount + shipping;

  return (
    <div data-testid="cart-order-summary" className="border border-gray-200 rounded-lg p-6 bg-white sticky top-4">
      <h2 className="text-xl font-semibold mb-4 text-gray-800">Resumen del Pedido</h2>

      {/* Subtotal */}
      <div className="flex justify-between mb-3 text-gray-700">
        <span>
          Subtotal ({cartItems.length} {cartItems.length === 1 ? 'producto' : 'productos'}):
        </span>
        <span className="font-medium">{toCurrencyFormat(subtotal)}</span>
      </div>

      {/* Cart Discounts */}
      {cartDiscounts > 0 && (
        <div className="flex justify-between mb-3 text-success">
          <span>Descuentos aplicados:</span>
          <span className="font-medium">-{toCurrencyFormat(cartDiscounts)}</span>
        </div>
      )}

      {/* Shipping Method Selector */}
      <CartShippingMethodSelector
        selectedShipping={selectedShipping || { id: '', code: '', name: '', description: '', cost: 0 }}
        onShippingChange={setSelectedShipping}
        subtotal={subtotal}
        preloadedLocation={selectedLocation || undefined}
        onLocationSelected={handleLocationSelected}
        preloadedMethods={shippingMethods.length > 0 ? shippingMethods : undefined}
      />

      {/* Shipping */}
      <div className="flex justify-between mb-3 text-gray-700">
        <span>Envío:</span>
        <span className="font-medium">{shipping === 0 ? <span className="text-success">Gratis</span> : toCurrencyFormat(shipping)}</span>
      </div>

      {/* Tax - for information only, already included in price */}
      <div className="flex justify-between mb-3 text-gray-500 text-sm">
        <span>IVA incluido (21%):</span>
        <span>{toCurrencyFormat(tax)}</span>
      </div>

      {/* Coupon Component */}
      <CartCoupon appliedCoupon={appliedCoupon} onCouponApplied={handleCouponApplied} onCouponRemoved={handleCouponRemoved} discount={couponDiscount} />

      {/* Divider */}
      <div className="border-t border-gray-200 my-4"></div>

      {/* Total */}
      <div className="flex justify-between mb-6 text-lg font-bold text-gray-900">
        <span>Total:</span>
        <span>{toCurrencyFormat(total)}</span>
      </div>

      {/* Checkout Button */}
      <button
        onClick={handleCheckout}
        disabled={cartItems.length === 0 || isLoading}
        data-testid="cart-proceed-button"
        className={`
                    w-full py-3 px-4 rounded-lg font-semibold text-white
                    transition-all duration-200
                    ${cartItems.length === 0 || isLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary hover:bg-primary/90 active:scale-95'}
                `}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Procesando...
          </span>
        ) : (
          'Proceder al Pago'
        )}
      </button>

      {/* Continue Shopping Link */}
      <button onClick={() => router.push('/search')} className="w-full mt-3 py-2 text-primary hover:text-primary/80 font-medium transition-colors">
        Continuar Comprando
      </button>

      {/* Security Badge */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          <span>Pago seguro y protegido</span>
        </div>
      </div>
    </div>
  );
};

export default CartOrderSummary;
